import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, SITE_ORIGIN } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimpleIcon, XCircleIcon } from "@phosphor-icons/react";

/**
 * Inline "Quick Add Listing" modal — shown from inside a live stream so the
 * seller never has to navigate away (and lose their broadcast). On success
 * we call onCreated() so LiveStream can refresh the shop list without a
 * page reload. Mirrors the minimum fields from /sell/new and attaches the
 * new listing to the active stream via the stream_id payload field.
 */
export default function QuickAddListingModal({ open, onClose, streamId, onCreated, prefill }) {
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    type: "auction",
    price: "",
    starting_bid: "",
    quantity: 1,
    auction_duration_seconds: 30,
  });

  useEffect(() => {
    if (!open) return;
    api
      .get("/categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => {});
  }, [open]);

  // Reset state every time the modal opens so the seller always starts fresh.
  // When a `prefill` is supplied (e.g. from the AI auto-detect popup's
  // "Edit" path), seed the form with the model's suggestion so the
  // seller only edits what's wrong.
  useEffect(() => {
    if (open) {
      setImages([]);
      const base = {
        title: "",
        description: "",
        category: "",
        type: "auction",
        price: "",
        starting_bid: "",
        quantity: 1,
        auction_duration_seconds: 30,
      };
      if (prefill) {
        base.title = prefill.title || "";
        base.description = prefill.reasoning || prefill.title || "";
        base.category = prefill.category || "";
        base.type = prefill.type || prefill.suggested_type || "fixed";
        const p = Number(prefill.suggested_price || prefill.price || 0);
        if (p > 0) base.price = String(p);
      }
      setForm(base);
    }
  }, [open, prefill]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploads = await Promise.all(
        files.map(async (f) => {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("purpose", "listing_image");
          const { data } = await api.post("/files/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return data.path;
        })
      );
      setImages((prev) => [...prev, ...uploads]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (p) => setImages((prev) => prev.filter((x) => x !== p));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.price) {
      toast.error("Title, category, and price are required.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        title: form.title,
        description: form.description || form.title,
        category: form.category,
        price: parseFloat(form.price),
        type: form.type,
        quantity: parseInt(form.quantity || 1, 10) || 1,
        images,
        stream_id: streamId,
      };
      if (form.type === "auction") {
        body.starting_bid = parseFloat(form.starting_bid || form.price);
        body.auction_duration_seconds = parseInt(form.auction_duration_seconds || 30, 10);
      }
      const { data } = await api.post("/listings", body);
      toast.success("Added to live!");
      onCreated && onCreated(data);
      onClose && onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="quick-add-listing-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight">
            Add a new item to live
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Drops directly into your shop without leaving the stream.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="quick-add-listing-form">
          <div>
            <Label className="overline">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              required
              className="mt-1"
              placeholder="e.g. 2024 Topps Chrome PSA 10"
              data-testid="quick-add-title-input"
            />
          </div>

          <div>
            <Label className="overline">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={2}
              className="mt-1"
              placeholder="Optional — defaults to the title."
              data-testid="quick-add-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Category</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger className="mt-1" data-testid="quick-add-category-select">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Type</Label>
              <Select value={form.type} onValueChange={(v) => update("type", v)}>
                <SelectTrigger className="mt-1" data-testid="quick-add-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auction">Live Auction</SelectItem>
                  <SelectItem value="fixed">Buy Now</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">
                {form.type === "fixed" ? "Price (USD)" : "Buy-now / Reserve"}
              </Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={form.price}
                onChange={(e) => update("price", e.target.value)}
                required
                className="mt-1"
                data-testid="quick-add-price-input"
              />
            </div>
            <div>
              <Label className="overline">Quantity</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={form.quantity}
                onChange={(e) => update("quantity", e.target.value)}
                required
                className="mt-1"
                data-testid="quick-add-quantity-input"
              />
            </div>
          </div>

          {form.type === "auction" && (
            <div>
              <Label className="overline">Starting bid (USD)</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={form.starting_bid}
                onChange={(e) => update("starting_bid", e.target.value)}
                className="mt-1"
                placeholder="Defaults to price"
                data-testid="quick-add-starting-bid-input"
              />
            </div>
          )}

          {form.type === "auction" && (
            <div>
              <Label className="overline">Duration</Label>
              <Select
                value={String(form.auction_duration_seconds)}
                onValueChange={(v) => update("auction_duration_seconds", parseInt(v, 10))}
              >
                <SelectTrigger className="mt-1" data-testid="quick-add-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 seconds</SelectItem>
                  <SelectItem value="5">5 seconds</SelectItem>
                  <SelectItem value="10">10 seconds</SelectItem>
                  <SelectItem value="30">30 seconds</SelectItem>
                  <SelectItem value="60">1 minute</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="overline">Photos</Label>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {images.map((p) => (
                <div
                  key={p}
                  className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200"
                >
                  <img
                    src={`${SITE_ORIGIN}/api/files/${p}`}
                    className="h-full w-full object-cover"
                    alt=""
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(p)}
                    className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5"
                    data-testid={`quick-add-remove-image-${p}`}
                  >
                    <XCircleIcon size={16} />
                  </button>
                </div>
              ))}
              <label className="aspect-square rounded-lg border-2 border-dashed border-zinc-300 grid place-items-center cursor-pointer hover:border-[#1D78AB]">
                <UploadSimpleIcon size={22} className="text-zinc-500" />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleUpload}
                  data-testid="quick-add-image-input"
                />
              </label>
            </div>
            {uploading && (
              <p className="text-xs text-zinc-500 mt-1">Uploading…</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-11 rounded-full"
              data-testid="quick-add-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || uploading}
              className="flex-1 h-11 bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full font-bold"
              data-testid="quick-add-submit-btn"
            >
              {submitting ? "Adding…" : "Drop into stream"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
